import { useEffect, useState } from "react";
import { motion, useSpring } from "framer-motion";

/** Custom cursor: small dot + expanding ring on hover, with dynamic label. */
export default function CustomCursor() {
  const [label, setLabel] = useState("");
  const [variant, setVariant] = useState("default"); // default | hover | text
  const [visible, setVisible] = useState(false);

  const sx = useSpring(0, { stiffness: 500, damping: 40, mass: 0.5 });
  const sy = useSpring(0, { stiffness: 500, damping: 40, mass: 0.5 });
  const rx = useSpring(0, { stiffness: 250, damping: 25, mass: 0.6 });
  const ry = useSpring(0, { stiffness: 250, damping: 25, mass: 0.6 });

  useEffect(() => {
    // Feature detect: only enable on hover-capable pointers
    if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
    document.body.classList.add("has-custom-cursor");

    function onMove(e) {
      sx.set(e.clientX);
      sy.set(e.clientY);
      rx.set(e.clientX);
      ry.set(e.clientY);
      if (!visible) setVisible(true);
    }

    function onOver(e) {
      const el = e.target.closest("[data-cursor]");
      if (el) {
        const c = el.dataset.cursor;
        setVariant(c === "text" ? "text" : "hover");
        setLabel(el.dataset.cursorLabel || "");
      } else if (e.target.closest("a, button, [role='button']")) {
        setVariant("hover");
        setLabel("");
      } else {
        setVariant("default");
        setLabel("");
      }
    }

    function onLeave() { setVisible(false); }

    window.addEventListener("mousemove", onMove, { passive: true });
    document.addEventListener("mouseover", onOver);
    document.addEventListener("mouseleave", onLeave);
    return () => {
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseover", onOver);
      document.removeEventListener("mouseleave", onLeave);
      document.body.classList.remove("has-custom-cursor");
    };
  }, [sx, sy, rx, ry, visible]);

  const dotSize = variant === "text" ? 6 : variant === "hover" ? 4 : 6;
  const ringSize = variant === "hover" ? 80 : variant === "text" ? 24 : 34;

  return (
    <>
      {/* Ring (slow) */}
      <motion.div
        aria-hidden
        style={{
          x: rx, y: ry,
          translateX: "-50%", translateY: "-50%",
          width: ringSize, height: ringSize,
          opacity: visible ? (variant === "hover" ? 1 : 0.6) : 0,
        }}
        className="pointer-events-none fixed top-0 left-0 z-[9999] rounded-full border transition-[width,height,opacity,background-color] duration-200 ease-out flex items-center justify-center"
      >
        {variant === "hover" && label && (
          <span className="font-mono text-[9px] uppercase tracking-[0.24em]" style={{ color: "var(--brass)" }}>{label}</span>
        )}
        <style>{`
          .pointer-events-none.fixed.top-0.left-0.z-\\[9999\\] { border-color: var(--gold); background: transparent; box-shadow: 0 0 12px rgba(198,168,124,0.35); mix-blend-mode: difference; }
        `}</style>
      </motion.div>
      {/* Dot (fast) */}
      <motion.div
        aria-hidden
        style={{
          x: sx, y: sy,
          translateX: "-50%", translateY: "-50%",
          width: dotSize, height: dotSize,
          background: "var(--gold)",
          opacity: visible ? 1 : 0,
        }}
        className="pointer-events-none fixed top-0 left-0 z-[9999] rounded-full transition-opacity duration-150"
      />
    </>
  );
}
